import { createRef } from '@wix/materializer'
import { REF_DELIMITER } from '@wix/thunderbolt-commons'
import type { Component, InternalRefDataItem, QueryProperty, WidgetRefDataItem } from '@wix/thunderbolt-becky-types'
import type { GhostPropertiesItem } from '@wix/thunderbolt-becky-root'
import type { ScopedCreateRef } from './compNodeUtils'
import { LEAFS_OVERRIDES_DELIMITER, RESOLVED_REFS_NAMESPACE } from './constants'

export const isRefComponent = (component: Component) => component.componentType.endsWith('RefComponent')

export const getRefCreatorInNamespace = (namespace: string): ScopedCreateRef => (key2, key3, defaultValue) =>
	createRef([namespace, key2, key3], defaultValue)

export const isLayoutOrStyleLeafOverride = (
	queryName: QueryProperty,
	id: string
): queryName is 'layoutQuery' | 'styleId' => id.includes(`-${queryName}-`)

export const getContextCompIdFromOverrideDataId = (overrideDataId: string, queryName: QueryProperty) => {
	const contextEndIndex = overrideDataId.lastIndexOf(REF_DELIMITER)
	const compIdIdx = contextEndIndex + REF_DELIMITER.length
	const queryNameIdx = overrideDataId.indexOf(`-${queryName}`, compIdIdx)

	return {
		compId: overrideDataId.substring(compIdIdx, queryNameIdx),
		context: overrideDataId.substring(0, contextEndIndex),
	}
}

export const getContextAndTemplateCompIdFromInflatedCompId = (inflatedCompId: string) => {
	const contextEndIndex = inflatedCompId.lastIndexOf(REF_DELIMITER)
	return {
		templateCompId: inflatedCompId.substring(contextEndIndex + REF_DELIMITER.length),
		context: inflatedCompId.substring(0, contextEndIndex),
	}
}

// "ref1_r_ref2 => ["ref1", "ref1_r_ref2"]
export const getRefCompIds = (refCompId: string): Array<string> => {
	const res: Array<string> = []
	refCompId.split(REF_DELIMITER).forEach((refId, index) => {
		if (index > 0) {
			res.push(`${res[index - 1]}${REF_DELIMITER}${refId}`)
		} else {
			res.push(refId)
		}
	})
	return res
}

export const getLeafsOverridesMapAspectName = (context: string, queryName: 'layoutQuery' | 'styleId') =>
	`${context}${LEAFS_OVERRIDES_DELIMITER}${queryName}LeafsOverridesMap`

export const getQueryNameFromLeafsOverridesMapAspectName = (aspectName: string) =>
	aspectName.split(LEAFS_OVERRIDES_DELIMITER)[1].replace('LeafsOverridesMap', '') as QueryProperty

export const getRefTemplateKey = (refDataItem: WidgetRefDataItem | InternalRefDataItem) =>
	refDataItem.type === 'WidgetRef'
		? `${refDataItem.appDefinitionId}_${refDataItem.widgetId}`
		: `${refDataItem.pageId || 'masterPage'}_${refDataItem.rootCompId}`

export const createRefInResolvedRefsNamespaces = getRefCreatorInNamespace(RESOLVED_REFS_NAMESPACE)

export const isGhostPropertyItem = (propItem?: GhostPropertiesItem) => propItem?.ghost === 'COLLAPSED'
